/**
 * MyProductsPage — member's digital-product storefront manager.
 * Path: /dashboard/my-products
 *
 * Lets the member:
 *  - Upload a PDF / EPUB / MP3 / ZIP / etc. (max 200 MB)
 *  - Set a cover image, name, description, price
 *  - Get a public buy link they can paste into Funnel Builder buy buttons,
 *    social posts, or anywhere they want to sell
 *  - See sales + download counts
 *  - Delete (existing buyers keep access)
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import SiteNav from "../components/SiteNav";
import { Upload, FileText, DollarSign, Plus, Trash2, Copy, Check, ImageIcon, X } from "lucide-react";

const NAVY = "#0F172A";
const GOLD = "#2E9F3A";
const RED = "#C9202E";
const CREAM = "#FFFFFF";

export default function MyProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", price: 17 });
  const [coverImage, setCoverImage] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [productFile, setProductFile] = useState(null);
  const [copied, setCopied] = useState("");
  const imgInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/digital-products");
      setProducts(data.products || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load your products.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const uploadCover = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCoverImage(r.data?.url || "");
      setCoverFile(file);
    } catch {
      toast.error("Cover image upload failed.");
    }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Give your product a name."); return; }
    if (!productFile) { toast.error("Pick the file (PDF/EPUB/MP3/etc.) you're selling."); return; }
    if (form.price < 0) { toast.error("Price can't be negative."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("description", form.description);
      fd.append("price_usd", String(form.price));
      fd.append("image_url", coverImage || "");
      fd.append("file", productFile);
      await api.post("/me/digital-products", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`✓ "${form.name}" is live and ready to sell.`);
      setShowForm(false);
      setForm({ name: "", description: "", price: 17 });
      setCoverImage(""); setCoverFile(null); setProductFile(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (pid, name) => {
    if (!window.confirm(`Remove "${name}"?\nExisting buyers will still keep access — but new buys will be blocked.`)) return;
    try {
      await api.delete(`/me/digital-products/${pid}`);
      toast.success("Removed.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete.");
    }
  };

  const copyLink = (pid) => {
    const url = `${window.location.origin}/buy/${pid}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(pid);
      toast.success("Buy link copied!");
      setTimeout(() => setCopied(""), 1500);
    });
  };

  return (
    <div className="min-h-screen" style={{ background: CREAM }}>
      <SiteNav />

      <section className="py-12" style={{ background: "#FFFFFF" }}>
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-[11px] tracking-[0.28em] uppercase font-bold mb-2" style={{ color: GOLD }}>★ Build · My Products</div>
          <h1 className="font-serif-display text-4xl md:text-5xl uppercase text-white leading-tight">Sell Your Stuff</h1>
          <div className="h-px w-16 mt-3" style={{ background: GOLD }} />
          <p className="mt-4 text-base md:text-lg max-w-2xl" style={{ color: "#E6E2D2" }}>
            Upload your book, PDF, audio, or course bundle. Faitharama handles the file delivery.
            You get a buy link to drop in your funnels or share anywhere.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 md:px-8 py-10">
        <div className="flex items-center justify-between gap-3 mb-6">
          <h2 className="font-serif-display text-2xl text-red-wine uppercase">Your Products ({products.length})</h2>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-bold uppercase tracking-[0.18em] text-sm shadow-md"
              style={{ background: RED, color: "white" }}
              data-testid="my-products-add"
            >
              <Plus className="w-4 h-4" /> Add Product
            </button>
          )}
        </div>

        {showForm && (
          <div className="mb-8 rounded-xl border-2 p-5 md:p-7 bg-white" style={{ borderColor: GOLD }} data-testid="my-products-form">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-lg text-red-wine">New Digital Product</h3>
              <button onClick={() => setShowForm(false)} className="text-foreground/50 hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid md:grid-cols-[180px_1fr] gap-5">
              <div>
                <div className="text-[10px] uppercase tracking-wider-cap font-bold text-foreground/70 mb-2">Cover Image</div>
                {coverImage ? (
                  <img src={coverImage} alt="" className="w-full aspect-square object-contain p-3 rounded-lg border bg-white" style={{ borderColor: GOLD }} />
                ) : (
                  <div className="w-full aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-foreground/40" style={{ borderColor: GOLD, background: "#EAF6FC" }}>
                    <ImageIcon className="w-8 h-8 mb-1" />
                    <div className="text-[10px] uppercase tracking-wider-cap text-center px-2">No cover yet</div>
                  </div>
                )}
                <button onClick={() => imgInputRef.current?.click()}
                        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[10px] uppercase tracking-wider-cap font-bold border-2 hover:bg-white"
                        style={{ borderColor: GOLD, color: "#9C6B14" }}
                        data-testid="my-products-cover-upload">
                  <Upload className="w-3 h-3" /> {coverImage ? "Replace cover" : "Upload cover"}
                </button>
                <input ref={imgInputRef} type="file" accept="image/*" onChange={(e) => uploadCover(e.target.files?.[0])} className="hidden" />
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider-cap font-bold text-foreground/70">Product name</label>
                  <input type="text" value={form.name}
                         onChange={(e) => setForm({ ...form, name: e.target.value })}
                         placeholder="My 30-Day Devotional"
                         className="w-full mt-1 text-lg font-bold border-b-2 border-foreground/15 focus:border-[#2E9F3A] focus:outline-none pb-1 bg-transparent"
                         data-testid="my-products-name" />
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-wider-cap font-bold text-foreground/70">Description</label>
                  <textarea rows={3} value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="One paragraph: what's in it, what they get, why they need it."
                            className="w-full mt-1 text-base border border-foreground/15 rounded-md px-3 py-2 focus:border-[#2E9F3A] focus:outline-none resize-none"
                            data-testid="my-products-desc" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider-cap font-bold text-foreground/70">Price (USD)</label>
                    <div className="flex items-center gap-1 mt-1">
                      <DollarSign className="w-5 h-5 text-red-wine" />
                      <input type="number" min="0" step="1" value={form.price}
                             onChange={(e) => setForm({ ...form, price: Number(e.target.value) || 0 })}
                             className="flex-1 text-2xl font-bold border-b-2 border-foreground/15 focus:border-[#2E9F3A] focus:outline-none bg-transparent"
                             data-testid="my-products-price" />
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <label className="text-[10px] uppercase tracking-wider-cap font-bold text-foreground/70">The actual file</label>
                    <button onClick={() => fileInputRef.current?.click()}
                            className="mt-1 flex-1 inline-flex items-center justify-center gap-2 px-3 py-3 rounded-md text-sm font-bold border-2"
                            style={{ borderColor: productFile ? "#2E9F3A" : GOLD, background: productFile ? "#EAF7EC" : "#FFFFFF", color: productFile ? "#1F7129" : "#9C6B14" }}
                            data-testid="my-products-file-pick">
                      {productFile ? <><Check className="w-4 h-4" /> {productFile.name.slice(0, 28)}</> : <><FileText className="w-4 h-4" /> Pick a file</>}
                    </button>
                    <input ref={fileInputRef} type="file"
                           accept=".pdf,.epub,.mobi,.mp3,.m4a,.wav,.zip,.txt,.doc,.docx"
                           onChange={(e) => setProductFile(e.target.files?.[0] || null)}
                           className="hidden" />
                    <div className="text-[10px] text-foreground/50 mt-1">PDF, EPUB, MP3, ZIP, etc. · Max 200 MB</div>
                  </div>
                </div>

                <button onClick={submit} disabled={busy}
                        className="w-full mt-3 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md font-bold uppercase tracking-[0.18em] text-sm shadow-md text-white disabled:opacity-60"
                        style={{ background: RED }}
                        data-testid="my-products-save">
                  {busy ? "Uploading…" : "★ Publish Product"}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-foreground/60">Loading your products…</div>
        ) : products.length === 0 ? (
          <div className="rounded-xl bg-white border-2 border-dashed p-10 text-center" style={{ borderColor: GOLD }}>
            <FileText className="w-12 h-12 mx-auto mb-3" style={{ color: GOLD }} />
            <div className="font-bold text-lg text-red-wine">No products yet.</div>
            <p className="text-foreground/70 mt-1">Upload your first digital product — book, PDF, audio, or course — and we'll handle the delivery.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {products.map((p) => (
              <div key={p.id} className="rounded-lg bg-white border p-4 md:p-5 flex flex-col md:flex-row gap-4" style={{ borderColor: "rgba(19,37,74,0.15)" }} data-testid={`my-product-${p.id}`}>
                {p.image_url ? (
                  <img src={p.image_url} alt="" className="w-24 h-24 object-contain p-1.5 rounded-md flex-shrink-0 bg-white border" />
                ) : (
                  <div className="w-24 h-24 bg-[#F4ECD8] rounded-md flex items-center justify-center flex-shrink-0">
                    <FileText className="w-8 h-8 text-foreground/40" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="font-bold text-lg text-red-wine">{p.name}</div>
                  {p.description && <p className="text-sm text-foreground/75 mt-1 line-clamp-2">{p.description}</p>}
                  <div className="flex items-center gap-4 mt-2 text-xs">
                    <span className="font-bold text-red-wine">${p.price_usd}</span>
                    <span className="text-foreground/60">{p.purchases_total || 0} sale{p.purchases_total === 1 ? "" : "s"}</span>
                    <span className="text-foreground/60">{p.downloads_total || 0} downloads</span>
                    <span className="text-foreground/60">{p.file_size_mb} MB</span>
                  </div>
                </div>
                <div className="flex md:flex-col gap-2 md:w-44">
                  <button onClick={() => copyLink(p.id)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs uppercase tracking-wider font-bold"
                          style={{ background: copied === p.id ? "#2E9F3A" : GOLD, color: "white" }}
                          data-testid={`my-product-copy-${p.id}`}>
                    {copied === p.id ? <><Check className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy buy link</>}
                  </button>
                  <a href={`/buy/${p.id}`} target="_blank" rel="noopener noreferrer"
                     className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs uppercase tracking-wider font-bold border-2"
                     style={{ borderColor: NAVY, color: NAVY }}
                     data-testid={`my-product-view-${p.id}`}>
                    Preview
                  </a>
                  <button onClick={() => remove(p.id, p.name)}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs uppercase tracking-wider font-bold text-red-wine hover:bg-red-50"
                          data-testid={`my-product-delete-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
